/**
 * Export planned workouts to FIT and TCX for head units (Garmin, Wahoo, etc.).
 */
import { getWorkoutSegments } from './workoutSegments';

const SPORT_TCX = {
  cycling: 'Biking',
  running: 'Running',
  mtb: 'Biking',
  moto: 'Other',
  ski: 'Other',
  swimming: 'Other',
  strength: 'Other',
  yoga: 'Other',
  other: 'Other',
};

/**
 * Build TCX 2.0 XML for a planned workout (Activity with one Lap per segment).
 * Compatible with Garmin Connect, Wahoo, Polar, etc.
 * @param {Object} workout - PlannedWorkout
 * @returns {string} TCX XML string
 */
export function buildTcxWorkout(workout) {
  const segments = getWorkoutSegments(workout);
  const sport = SPORT_TCX[workout?.sport] || 'Biking';
  const name = workout?.name || 'Workout';
  const description = workout?.description || '';

  const lapXml = segments
    .map((seg) => {
      const durationSeconds = Math.round(seg.end - seg.start);
      const intensity = seg.type === 'rest' ? 'Resting' : 'Active';
      return `        <Lap StartTime="${escapeXmlAttr(toIsoTime(workout))}">
          <TotalTimeSeconds>${durationSeconds}</TotalTimeSeconds>
          <Intensity>${intensity}</Intensity>
          <TriggerMethod>Manual</TriggerMethod>
          <Notes>${escapeXml(seg.label)} (Zone ${seg.zone})</Notes>
        </Lap>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2 https://www8.garmin.com/xmlschemas/TrainingCenterDatabasev2.xsd">
  <Activities>
    ${buildTcxActivityFragment(workout)}
  </Activities>
</TrainingCenterDatabase>`;
}

/**
 * Single Activity XML fragment for one workout (for multi-activity TCX).
 * @param {Object} workout - PlannedWorkout
 * @returns {string} <Activity>...</Activity> fragment
 */
export function buildTcxActivityFragment(workout) {
  const segments = getWorkoutSegments(workout);
  const sport = SPORT_TCX[workout?.sport] || 'Biking';
  const name = workout?.name || 'Workout';
  const description = workout?.description || '';

  const lapXml = segments
    .map((seg) => {
      const durationSeconds = Math.round(seg.end - seg.start);
      const intensity = seg.type === 'rest' ? 'Resting' : 'Active';
      return `      <Lap StartTime="${escapeXmlAttr(toIsoTime(workout))}">
          <TotalTimeSeconds>${durationSeconds}</TotalTimeSeconds>
          <Intensity>${intensity}</Intensity>
          <TriggerMethod>Manual</TriggerMethod>
          <Notes>${escapeXml(seg.label)} (Zone ${seg.zone})</Notes>
        </Lap>`;
    })
    .join('\n');

  return `  <Activity Sport="${escapeXmlAttr(sport)}">
      <Id>${escapeXmlAttr(toIsoTime(workout))}</Id>
      <Notes>${escapeXml(description || name)}</Notes>
${lapXml}
    </Activity>`;
}

/**
 * Build TCX 2.0 XML for a full training plan (multiple Activities in one file).
 * Common format for sharing/import into Garmin Connect, Wahoo, etc.
 * @param {Object} plan - Training plan doc { name, startDate, endDate, ... }
 * @param {Array<Object>} workouts - PlannedWorkout array (sorted by date)
 * @returns {string} TCX XML string
 */
export function buildPlanTcx(plan, workouts) {
  const list = Array.isArray(workouts) ? workouts : [];
  const activitiesXml = list.map((w) => buildTcxActivityFragment(w)).join('\n');
  const planName = plan?.name || 'Training Plan';
  return `<?xml version="1.0" encoding="UTF-8"?>
<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2 https://www8.garmin.com/xmlschemas/TrainingCenterDatabasev2.xsd">
  <Activities>
${activitiesXml}
  </Activities>
</TrainingCenterDatabase>`;
}

/**
 * Serialize a Firestore value (Timestamp, Date) to JSON-safe form.
 */
function toJsonSafe(value) {
  if (value == null) return value;
  if (typeof value?.toDate === 'function') return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(toJsonSafe);
  if (typeof value === 'object' && value.constructor === Object) {
    const out = {};
    for (const k of Object.keys(value)) out[k] = toJsonSafe(value[k]);
    return out;
  }
  return value;
}

/**
 * Build JSON for a full training plan (plan metadata + workouts array).
 * Common format for backup, sharing between apps, and APIs.
 * @param {Object} plan - Training plan doc { id, name, startDate, endDate, ... }
 * @param {Array<Object>} workouts - PlannedWorkout array
 * @returns {string} JSON string
 */
export function buildPlanJson(plan, workouts) {
  const list = Array.isArray(workouts) ? workouts : [];
  const payload = {
    format: 'training-plan',
    version: 1,
    exportedAt: new Date().toISOString(),
    plan: toJsonSafe({ ...plan }),
    workouts: list.map((w) => toJsonSafe({ ...w })),
  };
  return JSON.stringify(payload, null, 2);
}

function toIsoTime(workout) {
  const sd = workout?.scheduledDate;
  let date = new Date();
  if (sd) {
    if (typeof sd?.toDate === 'function') date = sd.toDate();
    else if (typeof sd === 'number') date = new Date(sd);
    else if (typeof sd === 'string') date = new Date(sd);
  }
  const time = workout?.scheduledTime || '09:00';
  const [h, m] = String(time).split(':').map(Number);
  date.setHours(isNaN(h) ? 9 : h, isNaN(m) ? 0 : m, 0, 0);
  return date.toISOString();
}

function escapeXml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function escapeXmlAttr(str) {
  return escapeXml(str);
}

/**
 * Build FIT file bytes for a planned workout (workout + workout_step messages).
 * Requires fit-encoder. Returns Uint8Array or null if encoder unavailable.
 * @param {Object} workout - PlannedWorkout (optional trainingZones for power/HR targets)
 * @param {Object} [options] - { trainingZones } for zone min/max (power/heart rate)
 * @returns {Promise<Uint8Array|null>}
 */
export async function buildFitWorkout(workout, options = {}) {
  try {
    const { FitEncoder, Message, FitMessages, FitConstants } = await import('fit-encoder');
    const segments = getWorkoutSegments(workout);
    const sport = mapSportToFit(workout?.sport);
    const name = workout?.name || 'Workout';
    const trainingZones = options?.trainingZones || {};

    const steps = segments.map((seg) => ({
      duration: Math.round(seg.end - seg.start),
      intensity: mapSegmentTypeToFitIntensity(seg.type),
      zone: seg.zone,
      powerRange: getZonePowerRange(seg.zone, trainingZones),
      hrRange: getZoneHrRange(seg.zone, trainingZones),
    }));

    const encoder = new FitEncoder();

    new Message(
      FitConstants.mesg_num.file_id,
      FitMessages.file_id,
      'time_created',
      'manufacturer',
      'product',
      'type'
    ).writeDataMessage(
      FitEncoder.toFitTimestamp(new Date()),
      1, // garmin
      0,
      FitConstants.file.workout
    );

    new Message(
      FitConstants.mesg_num.workout,
      FitMessages.workout,
      'wkt_name',
      'sport',
      'num_valid_steps'
    ).writeDataMessage(name, sport, steps.length);

    const workoutStepMessage = new Message(
      FitConstants.mesg_num.workout_step,
      FitMessages.workout_step,
      'custom_target_value_low',
      'custom_target_value_high',
      'secondary_target_type',
      'secondary_custom_target_value_low',
      'secondary_custom_target_value_high',
      'target_type',
      'duration_type',
      'duration_value',
      'target_value',
      'intensity',
      'message_index'
    );

    let messageIndex = 0;
    for (const step of steps) {
      const targetType = step.powerRange
        ? FitConstants.wkt_step_target.power
        : step.hrRange
          ? FitConstants.wkt_step_target.heart_rate
          : FitConstants.wkt_step_target.open;
      const customLow = step.powerRange
        ? step.powerRange.min
        : step.hrRange
          ? step.hrRange.min
          : undefined;
      const customHigh = step.powerRange
        ? step.powerRange.max
        : step.hrRange
          ? step.hrRange.max
          : undefined;

      workoutStepMessage.writeDataMessage(
        customLow ?? 0,
        customHigh ?? 0,
        FitConstants.wkt_step_target.open,
        undefined,
        undefined,
        targetType,
        FitConstants.wkt_step_duration.time,
        step.duration,
        0,
        step.intensity,
        messageIndex++
      );
    }

    const buffer = encoder.getFile();
    return new Uint8Array(buffer);
  } catch (e) {
    console.warn('FIT export unavailable:', e);
    return null;
  }
}

function mapSportToFit(sport) {
  const map = {
    cycling: 2,
    running: 1,
    mtb: 2,
    moto: 2,
    other: 0,
  };
  return map[sport] ?? 2;
}

function mapSegmentTypeToFitIntensity(type) {
  // FIT intensity: active=0, rest=1, warmup=2, cooldown=3, recovery=4, interval=5
  const map = {
    warmup: 2,
    cooldown: 3,
    rest: 1,
    recovery: 4,
    work: 5,
    segment: 0,
    main: 0,
  };
  return map[type] ?? 0;
}

function getZonePowerRange(zone, trainingZones) {
  const z = trainingZones?.powerZones?.[`zone${zone}`];
  return z && typeof z.min === 'number' && typeof z.max === 'number' ? { min: z.min, max: z.max } : null;
}

function getZoneHrRange(zone, trainingZones) {
  const z = trainingZones?.hrZones?.[`zone${zone}`];
  return z && typeof z.min === 'number' && typeof z.max === 'number' ? { min: z.min, max: z.max } : null;
}

/**
 * Trigger browser download of a workout file.
 * @param {Blob|Uint8Array|string} content - File content (Blob, binary, or string)
 * @param {string} filename - Suggested filename (e.g. "workout.fit", "workout.tcx")
 */
export function downloadWorkoutFile(content, filename) {
  const blob = content instanceof Blob
    ? content
    : typeof content === 'string'
      ? new Blob([content], { type: 'application/xml' })
      : new Blob([content], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
